import React from 'react';
import './BulkActionBar.scss';

/**
 * The bar that appears under the mail list while rows are selected: "<n> selected" plus the
 * actions that make sense in the open view. It is a plain flex item of the feed's column (not an
 * overlay), so it can never cover the last row and, when the phone keyboard is up, the feed
 * simply does not render it.
 *
 * Phone-first: every button is >=44px tall; under the phone breakpoint the actions get a row of
 * their own and share its width equally, so nothing scrolls sideways.
 *
 * Presentational only: the feed owns the selection and performs the moves.
 */
export interface BulkActionBarProps {
  /** Rows selected (a whole conversation counts once). */
  count: number;
  /** Messages selected; shown next to the count when it differs (a thread holds several). */
  messageCount?: number;
  /** While a batch runs: its progress line ("moving 7/12..."). The actions are disabled then. */
  busyLabel?: string | null;
  /** Is anything selected still unread? */
  canMarkRead: boolean;
  /** Which move actions apply in the open view (archive is pointless inside Archive, etc.). */
  showArchive: boolean;
  showDelete: boolean;
  showInbox: boolean;
  /** Every row in the view is already selected. */
  allSelected: boolean;
  onMarkRead: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onMoveToInbox: () => void;
  onSelectAll: () => void;
  /** Deselect everything and leave selection mode. */
  onClear: () => void;
  /** Bracket styling of the terminal theme; plain text when omitted (graphite). */
  br?: (label: string) => string;
}

const plain = (s: string): string => s;

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  count, messageCount, busyLabel, canMarkRead, showArchive, showDelete, showInbox, allSelected,
  onMarkRead, onArchive, onDelete, onMoveToInbox, onSelectAll, onClear, br = plain,
}) => {
  const busy = !!busyLabel;
  const countText = busyLabel
    || `${count} selected${messageCount !== undefined && messageCount > count ? ` (${messageCount} messages)` : ''}`;

  return (
    <div className="term-bulk" role="toolbar" aria-label="Bulk actions" data-busy={busy ? 'true' : undefined}>
      <span className="term-bulk__count" role="status" aria-live="polite">{countText}</span>

      {count > 0 && (
        <span className="term-bulk__actions">
          <button type="button" className="term-bulk__btn" onClick={onMarkRead} disabled={busy || !canMarkRead} title="Mark the selected messages as read">
            {br('mark read')}
          </button>
          {showArchive && (
            <button type="button" className="term-bulk__btn" onClick={onArchive} disabled={busy} title="Move the selected messages to Archive">
              {br('archive')}
            </button>
          )}
          {showInbox && (
            <button type="button" className="term-bulk__btn" onClick={onMoveToInbox} disabled={busy} title="Move the selected messages back to the inbox">
              {br('to inbox')}
            </button>
          )}
          {showDelete && (
            <button type="button" className="term-bulk__btn term-bulk__btn--danger" onClick={onDelete} disabled={busy} title="Move the selected messages to Deleted Items (you can undo)">
              {br('delete')}
            </button>
          )}
        </span>
      )}

      <span className="term-bulk__meta">
        <button type="button" className="term-bulk__btn" onClick={onSelectAll} disabled={busy || allSelected} title="Select every row in this view">
          {br('select all')}
        </button>
        <button type="button" className="term-bulk__btn" onClick={onClear} disabled={busy} title="Deselect everything and leave selection mode">
          {br(count > 0 ? 'clear' : 'done')}
        </button>
      </span>
    </div>
  );
};

export default BulkActionBar;
